import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../../api/axios';
import { useToast } from '../../context/ToastContext';
import { StatusBadge, PriorityBadge } from '../../components/StatusBadge';

const formatDuration = (from, to) => {
  if (!from) return '—';
  const start = new Date(from);
  const end = to ? new Date(to) : new Date();
  const mins = Math.floor((end - start) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
};

const AllTicketsAdmin = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    enterpriseId: '', kamId: '', agentId: '', status: '', priority: ''
  });

  const fetchTickets = useCallback(async (activeFilters) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(activeFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/api/admin/tickets', { params });
      setTickets(res.data);
    } catch {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTickets(filters); }, []); // eslint-disable-line

  const handleExportCsv = async () => {
    try {
      const res = await api.get('/api/admin/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tickets.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded successfully');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  const handleClear = () => {
    const cleared = { enterpriseId: '', kamId: '', agentId: '', status: '', priority: '' };
    setFilters(cleared);
    fetchTickets(cleared);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="w-full px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">All Tickets</h1>
            <p className="text-gray-500 text-sm">{tickets.length} tickets</p>
          </div>
          <button onClick={handleExportCsv}
            className="bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm">
            Export CSV
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <input type="text" placeholder="Enterprise ID" value={filters.enterpriseId}
              onChange={e => setFilters({ ...filters, enterpriseId: e.target.value })}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            <input type="text" placeholder="KAM ID" value={filters.kamId}
              onChange={e => setFilters({ ...filters, kamId: e.target.value })}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            <input type="text" placeholder="Agent ID" value={filters.agentId}
              onChange={e => setFilters({ ...filters, agentId: e.target.value })}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              <option value="">All Statuses</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="RESOLVED">Resolved</option>
            </select>
            <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              <option value="">All Priorities</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => fetchTickets(filters)}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-light transition-colors">
              Apply Filters
            </button>
            <button onClick={handleClear}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors">
              Clear
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {['Ticket ID', 'Enterprise', 'KAM', 'Type', 'Priority', 'Status', 'Agent', 'Created', 'Time Spent', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tickets.map(t => (
                  <tr key={t.ticketId} className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/tickets/${t.ticketId}`)}>
                    <td className="px-4 py-3 text-sm font-medium text-primary">{t.ticketId}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{t.enterpriseName}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{t.kamName}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{t.ticketType || '-'}</td>
                    <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                    <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-3 text-sm text-gray-500">{t.assignedAgentName || '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        t.status === 'RESOLVED'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-amber-50 text-amber-700'
                      }`}>
                        {formatDuration(t.createdAt, t.resolvedAt)}
                        {t.status !== 'RESOLVED' && <span className="ml-1 opacity-60">ongoing</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs text-primary font-medium hover:underline">View →</span>
                    </td>
                  </tr>
                ))}
                {tickets.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-8 text-gray-400">No tickets found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllTicketsAdmin;
